import {
  Machine,
  assign,
  AnyEventObject,
  ConditionPredicate,
  ActionFunctionMap,
  EventObject,
  TransitionConfigOrTarget,
  TransitionsConfig,
} from 'xstate';
import steps from './steps';

export type Context = {
  status: EnrollmentStatus;
  history: string[];
  hasFinancialSponsor: boolean;
};

export type EnrollmentStatus = 'failPayment' | 'pendingPayment' | 'success' | null;

export interface EnrollmentResultEvent extends AnyEventObject {
  status?: EnrollmentStatus;
}

export interface TargetSelectionEvent extends AnyEventObject {
  value?: string;
}

const resultPageSteps = [steps.success, steps.failPayment, steps.pendingPayment];

export const actions: ActionFunctionMap<Context, AnyEventObject> = {
  setStatus: assign({
    status: (_, event: EnrollmentResultEvent) => event.status || null,
  }),
  setHasFinancialSponsor: assign({
    hasFinancialSponsor: (_, event: TargetSelectionEvent) => event.value === 'financialSponsor',
  }),
  updateHistory: assign({
    history: (ctx, event, { state }) => {
      if (event.type !== 'NEXT') return ctx.history;

      const [currentStateMeta]: any[] = Object.values(state?.meta);
      const historySet = new Set(ctx.history);
      historySet.add(currentStateMeta.step);
      return Array.from(historySet);
    },
  }),
};

export const guards: Record<string, ConditionPredicate<Context, AnyEventObject>> = {
  evaluateStatus: (ctx, _, { cond }: any) => ctx.status === cond.status,
  hasEventProp: (_, event, { cond }: any) => {
    const hasProp = Object.keys(event).includes(cond.property);
    const hasValues = cond.values ? cond.values.includes(event[cond.property]) : true;
    return hasProp && hasValues;
  },
  canNavigateToStep: (ctx, _, { cond }: any) => {
    const isResultStep = resultPageSteps.includes(cond.step);
    const isAlreadyOnResultPage = ctx.status !== null;

    if (isResultStep && isAlreadyOnResultPage) return true;

    const lastReturnableStep = ctx.hasFinancialSponsor
      ? steps.financialSponsor.address
      : steps.academicFormation;

    const alreadyEnrolled = ctx.history.includes(lastReturnableStep);

    return !alreadyEnrolled && ctx.history.includes(cond.step);
  },
  selectTarget: (_, event: TargetSelectionEvent, { cond }: any) => event.value === cond.value,
};

export const returnableSteps = [
  steps.personalData,
  steps.address,
  steps.identificationDocument,
  steps.academicFormation,
  steps.financialSponsor.personalData,
  steps.financialSponsor.identificationDocument,
  steps.financialSponsor.address,
  ...resultPageSteps,
];

export const globalEvents: TransitionsConfig<Context, EventObject> = {
  ...returnableSteps.reduce((acc, step) => {
    const event: string = `GO_TO_${step.toUpperCase()}`;

    acc[event] = { target: step, cond: { type: 'canNavigateToStep', step } };

    return acc;
  }, {} as { [key: string]: TransitionConfigOrTarget<Context, EventObject> }),
};

export default Machine<Context>(
  {
    id: 'enrollments',
    initial: 'personalData',
    context: {
      status: null,
      history: [],
      hasFinancialSponsor: false,
    },
    states: {
      personalData: {
        meta: { step: steps.personalData },
        exit: ['updateHistory'],
        on: { NEXT: 'address' },
      },
      address: {
        meta: { step: steps.address },
        exit: ['updateHistory'],
        on: {
          PREV: 'personalData',
          NEXT: 'identificationDocument',
        },
      },
      identificationDocument: {
        meta: { step: steps.identificationDocument },
        exit: ['updateHistory'],
        on: {
          PREV: 'address',
          NEXT: 'academicFormation',
        },
      },
      academicFormation: {
        meta: { step: steps.academicFormation },
        exit: ['updateHistory'],
        on: {
          PREV: 'identificationDocument',
          NEXT: [
            {
              target: 'financialSponsor',
              cond: { type: 'selectTarget', value: 'financialSponsor' },
              actions: ['setHasFinancialSponsor'],
            },
            {
              target: 'documents',
              cond: { type: 'selectTarget', value: 'documents' },
              actions: ['setHasFinancialSponsor'],
            },
          ],
        },
      },
      financialSponsor: {
        initial: 'personalData',
        states: {
          personalData: {
            meta: { step: steps.financialSponsor.personalData },
            exit: ['updateHistory'],
            on: {
              PREV: '#enrollments.academicFormation',
              NEXT: 'identificationDocument',
            },
          },
          identificationDocument: {
            meta: { step: steps.financialSponsor.identificationDocument },
            exit: ['updateHistory'],
            on: {
              PREV: 'personalData',
              NEXT: 'address',
            },
          },
          address: {
            meta: { step: steps.financialSponsor.address },
            exit: ['updateHistory'],
            on: {
              PREV: 'identificationDocument',
              NEXT: '#enrollments.documents',
            },
          },
        },
      },
      documents: {
        meta: { step: steps.documents },
        exit: ['updateHistory'],
        on: {
          NEXT: 'contract',
        },
      },
      contract: {
        meta: { step: steps.contract },
        exit: ['updateHistory'],
        on: {
          NEXT: 'payment',
        },
      },
      payment: {
        meta: { step: steps.payment },
        exit: ['updateHistory'],
        on: {
          NEXT: {
            target: 'result',
            cond: {
              type: 'hasEventProp',
              property: 'status',
              values: ['failPayment', 'pendingPayment', 'success'] as EnrollmentStatus[],
            },
            actions: ['setStatus'],
          },
        },
      },
      result: {
        always: [
          {
            target: 'success',
            cond: { type: 'evaluateStatus', status: 'success' },
          },
          {
            target: 'pendingPayment',
            cond: { type: 'evaluateStatus', status: 'pendingPayment' },
          },
          {
            target: 'failPayment',
            cond: { type: 'evaluateStatus', status: 'failPayment' },
          },
        ],
      },
      failPayment: {
        meta: { step: steps.failPayment },
        entry: ['updateHistory'],
        on: {
          NEXT: {
            target: 'payment',
            actions: ['checkIfNeedAction'],
          },
        },
      },
      success: {
        meta: { step: steps.success },
        entry: ['updateHistory'],
        on: {
          NEXT: [
            {
              target: 'accounts',
              cond: { type: 'selectTarget', value: 'accounts' },
            },
            {
              target: 'accountsResult',
              cond: { type: 'selectTarget', value: 'accountsResult' },
            },
          ],
        },
      },
      pendingPayment: {
        meta: { step: steps.pendingPayment },
        entry: ['updateHistory'],
        on: {
          NEXT: [
            {
              target: 'accounts',
              cond: { type: 'selectTarget', value: 'accounts' },
            },
            {
              target: 'accountsResult',
              cond: { type: 'selectTarget', value: 'accountsResult' },
            },
          ],
        },
      },
      accounts: {
        meta: { step: steps.accounts },
        entry: ['updateHistory'],
        on: {
          NEXT: 'accountsResult',
        },
      },
      accountsResult: {
        meta: { step: steps.accountsResult },
        entry: ['updateHistory'],
      },
    },
    on: {
      ...globalEvents,
    },
  },
  {
    actions,
    guards,
  },
);
